import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Button } from "./button";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const sizeClasses = {
  sm: "max-w-[448px]",
  md: "max-w-[512px]",
  lg: "max-w-[672px]",
  xl: "max-w-[750px]",
  "2xl": "max-w-[1000px]",
  flow: "max-w-[1000px] h-[100dvh] sm:h-[640px]",
} as const;

type DialogSize = keyof typeof sizeClasses;

const DialogSizeContext = React.createContext<DialogSize>("md");

interface DialogScrollableContextValue {
  isScrollable: boolean;
  setIsScrollable: (value: boolean) => void;
}

const DialogScrollableContext = React.createContext<DialogScrollableContextValue>({
  isScrollable: false,
  setIsScrollable: () => {},
});

const DialogInsideHeaderContext = React.createContext(false);

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: DialogSize;
  }
>(({ className, size = "md", children, ...props }, ref) => {
  const [isScrollable, setIsScrollable] = React.useState(false);
  const scrollableValue = React.useMemo(() => ({ isScrollable, setIsScrollable }), [isScrollable]);

  return (
    <DialogSizeContext.Provider value={size}>
      <DialogScrollableContext.Provider value={scrollableValue}>
        <DialogPortal>
          <DialogOverlay />
          <DialogPrimitive.Content
            ref={ref}
            className={cn(
              "fixed z-50 flex flex-col gap-6 bg-white p-6 focus:outline-none",
              "inset-0 h-full w-full",
              "sm:inset-auto sm:left-1/2 sm:top-1/2 sm:h-auto sm:w-full sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-xl sm:border sm:border-primary sm:shadow-lg sm:duration-200",
              "sm:min-h-[200px] sm:max-h-[90dvh]",
              "sm:data-[state=open]:animate-in sm:data-[state=closed]:animate-out sm:data-[state=closed]:fade-out-0 sm:data-[state=open]:fade-in-0 sm:data-[state=closed]:zoom-out-95 sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:slide-out-to-left-1/2 sm:data-[state=closed]:slide-out-to-top-[48%] sm:data-[state=open]:slide-in-from-left-1/2 sm:data-[state=open]:slide-in-from-top-[48%]",
              sizeClasses[size],
              className,
            )}
            {...props}
          >
            {children}
          </DialogPrimitive.Content>
        </DialogPortal>
      </DialogScrollableContext.Provider>
    </DialogSizeContext.Provider>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const { isScrollable } = React.useContext(DialogScrollableContext);

  return (
    <DialogInsideHeaderContext.Provider value={true}>
      <div
        className={cn("flex items-start justify-between", isScrollable && "pb-6", className)}
        {...props}
      >
        <div className="flex-1">{children}</div>
        <DialogPrimitive.Close asChild>
          <IconButton label="Close" tooltip={false}>
            <X />
          </IconButton>
        </DialogPrimitive.Close>
      </div>
    </DialogInsideHeaderContext.Provider>
  );
};
DialogHeader.displayName = "DialogHeader";

const DialogBody = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const { isScrollable, setIsScrollable } = React.useContext(DialogScrollableContext);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => {
      setIsScrollable(el.scrollHeight > el.clientHeight);
    };

    check();

    const resizeObserver = new ResizeObserver(check);
    resizeObserver.observe(el);

    const mutationObserver = new MutationObserver(check);
    mutationObserver.observe(el, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [setIsScrollable]);

  return (
    <div
      ref={ref}
      className={cn(
        "flex-1 overflow-y-auto -mx-6 px-6",
        isScrollable && "-mt-6 -mb-6 border-t border-primary pt-6 pb-6",
        className,
      )}
      {...props}
    />
  );
};
DialogBody.displayName = "DialogBody";

interface DialogFooterProps {
  onSubmit: () => void;
  submitText?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  className?: string;
  variant?: "brand" | "default";
  divider?: boolean;
  rounded?: boolean;
  fullWidth?: boolean;
  children?: React.ReactNode;
}

const DialogFooter: React.FC<DialogFooterProps> = ({
  onSubmit,
  submitText = "Submit",
  disabled = false,
  loading = false,
  className,
  variant = "brand",
  divider = true,
  rounded = false,
  fullWidth = false,
  children,
}) => {
  const size = React.useContext(DialogSizeContext);
  const { isScrollable } = React.useContext(DialogScrollableContext);
  const isSmall = size === "sm";
  const buttonFullWidth = fullWidth || isSmall;
  const showDivider = divider && isScrollable;

  return (
    <div
      className={cn(
        "flex flex-col gap-3",
        showDivider && "-mx-6 border-t border-primary px-6",
        showDivider && (children ? "pt-3" : "pt-6"),
        className,
      )}
    >
      {children}
      <div className={cn("flex", buttonFullWidth ? "" : "justify-end")}>
        <Button
          variant={variant}
          rounded={rounded}
          className={buttonFullWidth ? "w-full" : ""}
          disabled={disabled || loading}
          onClick={onSubmit}
        >
          {loading ? "Loading..." : submitText}
        </Button>
      </div>
    </div>
  );
};
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, children, ...props }, ref) => {
  const { isScrollable } = React.useContext(DialogScrollableContext);
  const insideHeader = React.useContext(DialogInsideHeaderContext);
  const addPadding = isScrollable && !insideHeader;

  return (
    <DialogPrimitive.Title ref={ref} asChild {...props}>
      <h4 className={cn(addPadding && "pb-6", className)}>{children}</h4>
    </DialogPrimitive.Title>
  );
});
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-secondary", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
